import { companyTodayIsoDate } from '@/lib/company-date'
import type { AuditFilters, AuditLogResource } from './api'

export type AuditCardGroup = { id: string; rows: AuditLogResource[] }

export function groupAuditRows(rows: AuditLogResource[]): AuditCardGroup[] {
  const groups: AuditCardGroup[] = []
  for (const row of rows) {
    const previous = groups.at(-1)
    if (row.request_id && previous?.rows[0]?.request_id === row.request_id) previous.rows.push(row)
    else groups.push({ id: row.id, rows: [row] })
  }
  return groups
}

export function groupAuditDays(
  rows: AuditLogResource[],
  locale: string,
  now = new Date(),
): [string, AuditLogResource[]][] {
  const groups = new Map<string, AuditLogResource[]>()
  for (const row of rows) {
    const day = new Intl.DateTimeFormat('en-CA').format(new Date(row.occurred_at))
    groups.set(day, [...(groups.get(day) ?? []), row])
  }
  return [...groups.entries()].map(([day, items]) => {
    const date = new Date(`${day}T12:00:00Z`)
    const today = new Intl.DateTimeFormat('en-CA').format(now)
    const yesterday = new Date(now)
    yesterday.setDate(yesterday.getDate() - 1)
    const yesterdayText = new Intl.DateTimeFormat('en-CA').format(yesterday)
    return [
      day === today
        ? 'today'
        : day === yesterdayText
          ? 'yesterday'
          : new Intl.DateTimeFormat(locale, {
              weekday: 'long',
              day: 'numeric',
              month: 'long',
              year: 'numeric',
            }).format(date),
      items,
    ]
  })
}

export function presetRange(preset: 'today' | '7days' | '30days' | 'month', now = new Date()) {
  const to = companyTodayIsoDate(undefined, now)
  const start = new Date(`${to}T12:00:00`)
  if (preset === 'today') return { from: to, to }
  if (preset === 'month') start.setDate(1)
  else start.setDate(start.getDate() - (preset === '7days' ? 6 : 29))
  return {
    from: `${start.getFullYear()}-${String(start.getMonth() + 1).padStart(2, '0')}-${String(start.getDate()).padStart(2, '0')}`,
    to,
  }
}

export function cleanFilters(filters: AuditFilters): AuditFilters {
  return Object.fromEntries(
    Object.entries(filters).filter(([, value]) => value !== undefined && value !== ''),
  ) as AuditFilters
}

export function recordHref(type: string, id: string | null): string | null {
  if (!id) return null
  const routes: Record<string, string> = {
    employee: `/hr/employees/${id}`,
    employee_violation: `/hr/violations/${id}`,
    leave_request: `/hr/leave/requests/${id}`,
    expense_claim: `/expenses/claims/${id}`,
    asset: `/accounting/assets/${id}`,
  }
  return routes[type] ?? null
}
